/**
 * Voice names a reader can actually read.
 *
 * Engines name their voices for engineers. Edge offers "Microsoft Zofia Online
 * (Natural) - Polish (Poland)", which in a picker a few centimetres wide truncates
 * to "Microsoft Zofia On…" - the vendor and the delivery mechanism survive and the
 * one word that tells two voices apart does not. Every voice in the list is Polish,
 * because the app reads nothing else, so the language suffix says nothing either.
 *
 * What is kept is what distinguishes one voice from another: the given name and the
 * quality marker in brackets, so "Zofia (Natural)" stays apart from the plain local
 * "Zofia" beside it.
 */

/** A leading vendor, which every voice from that engine shares. */
const VENDOR_PREFIX = /^(?:Microsoft|Apple|Google(?=\s+\S+\s)|Samsung)\s+/i

/**
 * Words that describe how the voice is delivered rather than which voice it is.
 * "Online" is Edge's network voices; the matching "(Natural)" is kept, because that
 * one is the audible difference.
 */
const DELIVERY_WORDS = /\s+\b(?:Online|Desktop)\b/gi

/** " - Polish (Poland)", " - polski (Polska)", and the like: always Polish here. */
const LANGUAGE_SUFFIX = /\s+[-–—]\s+.*$/

export function shortVoiceName(name: string): string {
  const original = (name ?? '').trim()
  const short = original
    .replace(LANGUAGE_SUFFIX, '')
    .replace(VENDOR_PREFIX, '')
    .replace(DELIVERY_WORDS, '')
    .replace(/\s{2,}/g, ' ')
    .trim()

  // A name made entirely of the parts being removed - or an engine's internal id -
  // is better shown whole than as nothing.
  return short || original
}

export interface NamedVoice {
  voiceURI: string
  name: string
}

/**
 * Short labels for a whole list, keyed by voiceURI.
 *
 * Shortening can make two different voices read the same - Edge may list a local
 * "Microsoft Paulina" and a network "Microsoft Paulina Online" - and a picker with
 * two identical rows is a picker that cannot be used. Any label that collides falls
 * back to the voice's full name, for every voice sharing it, so each row stays
 * distinct and the reader can still tell which is which.
 */
export function shortVoiceNames(voices: readonly NamedVoice[]): Map<string, string> {
  const shortened = voices.map((voice) => ({ voice, label: shortVoiceName(voice.name) }))

  const counts = new Map<string, number>()
  for (const { label } of shortened) {
    const key = label.toLocaleLowerCase('pl')
    counts.set(key, (counts.get(key) ?? 0) + 1)
  }

  const labels = new Map<string, string>()
  for (const { voice, label } of shortened) {
    const collides = (counts.get(label.toLocaleLowerCase('pl')) ?? 0) > 1
    labels.set(voice.voiceURI, collides ? voice.name.trim() : label)
  }
  return labels
}
